import { secondsToTimecode } from '../../helpers/timecodeUtils';
import { historyStore, cleanItem } from '../historyStore';
import { queryClient, queryKeys } from '../../lib/query-client';
import ProjectService from '../../service/project-service';
import displayNetworkError from '../../helpers/displayNetworkError';
import handleErrorAndDisplay from '../../helpers/handleErrorAndDisplay';
import handleSuccessAndDisplay from '../../helpers/handleSuccessAndDisplay';
import { __, _n, sprintf } from '@wordpress/i18n';

export const makeCuesheetActions = (set, get) => {
    const fetchCueSheetImpl = async (id) => {
        set('cueSheetLoading', true);
        set('cueSheetError', null);

        try {
            const { items, history: historyData } = await queryClient.fetchQuery({
                queryKey: queryKeys.cueSheet(id),
                queryFn: async () => {
                    const [items, history] = await Promise.all([
                        ProjectService.getProjectCueSheetAsync(id),
                        ProjectService.getProjectHistoryAsync(id).catch(() => []),
                    ]);
                    return { items, history };
                },
            });

            const normalizedData = items.map(item => ({
                ...item,
                instanceDuration: item.instanceDuration ?? item.clip?.duration ?? 0,
                priority: item.priority ?? 0,
                startPlayback: item.startPlayback ?? 0,
            }));

            set('cueSheet', normalizedData);
            set('cueSheetLoading', false);

            const history = historyStore.getState();
            if (historyData && (Array.isArray(historyData) ? historyData.length > 0 : historyData?.body)) {
                history.load(historyData);
            } else {
                history.clear();
            }
        } catch (error) {
            set('cueSheetError', error);
            set('cueSheetLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const refetchCueSheetImpl = async (id) => {
        // Cue sheet writes (assign, upload, delete, import) emit activities, so the
        // Comm Center feeds are refreshed alongside.
        queryClient.invalidateQueries({ queryKey: [...queryKeys.project(id), 'activities'] });
        queryClient.invalidateQueries({ queryKey: ['me', 'activities'] });
        await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
        await fetchCueSheetImpl(id);
    };

    return {
        fetchCueSheetImpl,
        refetchCueSheetImpl,

        fetchCueSheet: fetchCueSheetImpl,
        refetchCueSheet: refetchCueSheetImpl,

        createCueSheetItem: async (id, formData) => {
            try {
                await ProjectService.createProjectCueSheetItemAsync(id, formData);

                await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
                await refetchCueSheetImpl(id);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        importCueSheetItems: async (id, formData) => {
            try {
                await ProjectService.importProjectCueSheetItemsAsync(id, formData);

                await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
                await refetchCueSheetImpl(id);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        deleteCueSheetItem: async (id, itemId) => {
            try {
                await ProjectService.deleteProjectCueSheetItemAsync(id, itemId);

                await refetchCueSheetImpl(id);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /**
         * Removes several cue sheet items in one request.
         *
         * `POST .../cue-sheet/bulk-delete` answers per item, so a group that
         * only partly went through is reported as such instead of looking like
         * a total success or a total failure.
         */
        bulkDeleteCueSheetItems: async (projectId, itemIds = []) => {
            const ids = itemIds.filter(Boolean);

            if (ids.length === 0) return { deleted: 0, failed: 0 };

            try {
                const response = await ProjectService.bulkDeleteProjectCueSheetItemsAsync(projectId, ids);
                const results = response?.results || [];
                const deleted = results.filter(result => result.deleted).length;
                const failed = results.filter(result => !result.deleted);

                if (deleted > 0) {
                    handleSuccessAndDisplay(sprintf(
                        _n('%d elemento eliminato', '%d elementi eliminati', deleted, 'soundbetween'),
                        deleted
                    ));
                }

                // The items that failed did so for their own reason; the first
                // one names it, the count says how many share the outcome.
                if (failed.length > 0) {
                    handleErrorAndDisplay(sprintf(
                        _n(
                            '%1$d elemento non eliminato: %2$s',
                            '%1$d elementi non eliminati: %2$s',
                            failed.length,
                            'soundbetween'
                        ),
                        failed.length,
                        failed[0].error || __('errore sconosciuto', 'soundbetween')
                    ));
                }

                await refetchCueSheetImpl(projectId);

                return { deleted, failed: failed.length };
            } catch (error) {
                displayNetworkError(error);
                await refetchCueSheetImpl(projectId);

                return { deleted: 0, failed: ids.length };
            }
        },

        /**
         * Sets the assignees of one cue sheet item. The list that is sent is the
         * list that stays: the modal opens on the current one and saves it whole,
         * so no diff is computed and no two calls can undo one another.
         *
         * @param {string[]} userIds  empty unassigns everybody
         * @param {string}   note     optional, reaches every newly added assignee
         */
        setCueSheetAssignees: async (projectId, cueSheetId, userIds = [], note = '') => {
            try {
                const ids = (Array.isArray(userIds) ? userIds : [userIds]).filter(Boolean);

                await ProjectService.setProjectCueSheetAssigneesAsync(projectId, cueSheetId, ids, note || null);

                handleSuccessAndDisplay(ids.length
                    ? sprintf(
                        _n('%d membro assegnato', '%d membri assegnati', ids.length, 'soundbetween'),
                        ids.length
                    )
                    : __('Assegnazione rimossa', 'soundbetween'));

                await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
                await refetchCueSheetImpl(projectId);

                return { assigned: ids.length };
            } catch (error) {
                displayNetworkError(error);

                // Flagged as handled: the caller keeps its form open, the error
                // has already been shown.
                throw Object.assign(error?.handled ? error : new Error('Assignment failed'), {
                    handled: true,
                    cause: error,
                });
            }
        },

        /** Drops one assignee from an item, leaving the others in place. */
        removeCueSheetAssignee: async (projectId, cueSheetId, userId) => {
            try {
                await ProjectService.removeProjectCueSheetAssigneeAsync(projectId, cueSheetId, userId);

                handleSuccessAndDisplay(__('Assegnazione rimossa', 'soundbetween'));

                await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
                await refetchCueSheetImpl(projectId);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /**
         * The same replace-set over a selection of cues, in one request: the
         * whole group is assigned to the same people and the notification goes
         * out once per new assignee.
         */
        bulkSetCueSheetAssignees: async (projectId, cueSheetIds = [], userIds = [], note = '') => {
            const ids = cueSheetIds.filter(Boolean);

            if (ids.length === 0) return { assigned: 0, failed: 0 };

            const userIdList = (Array.isArray(userIds) ? userIds : [userIds]).filter(Boolean);

            try {
                const response = await ProjectService.bulkSetProjectCueSheetAssigneesAsync(
                    projectId,
                    ids.map(cueSheetId => ({ cue_sheet_id: cueSheetId, user_ids: userIdList })),
                    note || null
                );

                const results = response?.results || [];
                const failed = results.filter(result => result.error);
                const assigned = (results.length || ids.length) - failed.length;

                if (assigned > 0) {
                    handleSuccessAndDisplay(userIdList.length
                        ? sprintf(
                            _n('%d cue assegnata', '%d cue assegnate', assigned, 'soundbetween'),
                            assigned
                        )
                        : sprintf(
                            _n(
                                'Assegnazione rimossa da %d cue',
                                'Assegnazione rimossa da %d cue',
                                assigned,
                                'soundbetween'
                            ),
                            assigned
                        ));
                }

                if (failed.length > 0) {
                    handleErrorAndDisplay(sprintf(
                        _n(
                            '%1$d cue non assegnata: %2$s',
                            '%1$d cue non assegnate: %2$s',
                            failed.length,
                            'soundbetween'
                        ),
                        failed.length,
                        failed[0].error || __('errore sconosciuto', 'soundbetween')
                    ));
                }

                await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
                await refetchCueSheetImpl(projectId);

                return { assigned, failed: failed.length };
            } catch (error) {
                displayNetworkError(error);

                throw Object.assign(error?.handled ? error : new Error('Bulk assignment failed'), {
                    handled: true,
                    cause: error,
                });
            }
        },

        saveCueSheetUpdates: async (projectId, updates) => {
            try {
                const currentCueSheet = get('cueSheet');
                const updatedCueSheet = currentCueSheet.map(item => {
                    const update = updates.find(u => u.id === item.id);
                    if (update) {
                        return {
                            ...item,
                            ...(update.title !== undefined && { title: update.title }),
                            ...(update.label !== undefined && { label: update.label }),
                            ...(update.timecode !== undefined && { timecode: update.timecode }),
                            ...(update.instanceDuration !== undefined && { instanceDuration: update.instanceDuration }),
                            ...(update.priority !== undefined && { priority: update.priority }),
                            ...(update.startPlayback !== undefined && { startPlayback: update.startPlayback }),
                            ...(update.status !== undefined && { status: update.status }),
                        };
                    }
                    return item;
                });
                set('cueSheet', updatedCueSheet);
                historyStore.getState().onCueSheetUpdated(projectId);
            } catch (error) {
                handleErrorAndDisplay(error);
                const refetchCueSheet = get('refetchCueSheet');
                if (refetchCueSheet) {
                    await refetchCueSheetImpl(projectId);
                }

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        cutCueSheetItem: async (projectId, originalItem, firstCutDuration, secondCutStart, secondCutDuration, secondCutStartPlayback, newId) => {
            const frameRate = 24;

            historyStore.getState().beginAction('Cut');

            const base = cleanItem(originalItem);

            const firstCut = {
                ...base,
                instanceDuration: Math.round(firstCutDuration * 1000),
            };

            const secondCut = {
                ...base,
                id: newId,
                timecode: secondsToTimecode(secondCutStart, frameRate),
                instanceDuration: Math.round(secondCutDuration * 1000),
                startPlayback: secondCutStartPlayback,
            };

            const currentCueSheet = get('cueSheet');
            const updatedCueSheet = [
                ...currentCueSheet.map(item => item.id === originalItem.id ? firstCut : item),
                secondCut,
            ];

            set('cueSheet', updatedCueSheet);
            historyStore.getState().onCueSheetUpdated(projectId);

            try {
                await ProjectService.cutProjectCueSheetItemAsync(
                    projectId,
                    originalItem.id,
                    firstCut,
                    secondCut,
                );
            } catch (error) {
                handleErrorAndDisplay(error);
                await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(projectId) });
            }
        },

        saveHistoryItems: async (projectId, items) => {
            if (!projectId) return;
            const { undoStack } = historyStore.getState();
            const cleanItems = items.map(cleanItem);
            const payload = {
                edit_history: { body: JSON.stringify(undoStack) },
                items: cleanItems,
            };
            await ProjectService.updateProjectCueSheetItemsAsync(projectId, payload);
        },

        syncCueSheetWithHistory: async (projectId, label, patch) => {
            const { undoStack } = historyStore.getState();
            const cueSheet = get('cueSheet');

            const changedIds = new Set([
                ...Object.keys(patch.changed),
                ...patch.added.map(i => i.id),
            ]);

            const items = cueSheet
                .filter(item => changedIds.has(item.id))
                .map(item => cleanItem(item));

            const payload = {
                edit_history: { body: JSON.stringify(undoStack) },
                items,
            };

            await ProjectService.updateProjectCueSheetItemsAsync(projectId, payload);
        },
    };
};
